import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { ATTRIBUTE_TYPES } from 'dashboard/components-next/ConversationWorkflow/constants';

/**
 * Composable for managing conversation required attributes workflow
 *
 * This handles the logic for checking if conversations have all required
 * custom attributes filled before they can be resolved.
 *
 * Teletel (owner decision, release audit 2026-08-15): visibility depends on
 * the actual required-attribute list managed by the Teletel endpoint — which
 * is stored in account.settings and delivered with the account payload — and
 * never on CONVERSATION_REQUIRED_ATTRIBUTES or any premium feature flag.
 * Fail-safe: while the account settings payload is not available the state is
 * reported as `unknown`; callers must surface an error instead of silently
 * assuming there are no required fields. The server-side Teletel validation
 * remains authoritative in every case.
 */
export function useConversationRequiredAttributes() {
  const { currentAccount } = useAccount();
  const conversationAttributes = useMapGetter(
    'attributes/getConversationAttributes'
  );

  // null/undefined settings => the account payload has not loaded (or failed
  // to load); an empty list inside loaded settings is a valid "no
  // requirements" state, not an error.
  const accountSettings = computed(() => currentAccount.value?.settings);
  const requirementsUnknown = computed(() => accountSettings.value == null);

  const requiredAttributeKeys = computed(
    () => accountSettings.value?.conversation_required_attributes || []
  );

  const allAttributeOptions = computed(() =>
    (conversationAttributes.value || []).map(attribute => ({
      ...attribute,
      value: attribute.attributeKey,
      label: attribute.attributeDisplayName,
      type: attribute.attributeDisplayType,
      attributeValues: attribute.attributeValues,
    }))
  );

  /**
   * Get the full attribute definitions for only the required attributes
   * Filters allAttributeOptions to only include attributes marked as required
   */
  const requiredAttributes = computed(
    () =>
      requiredAttributeKeys.value
        .map(key =>
          allAttributeOptions.value.find(attribute => attribute.value === key)
        )
        .filter(Boolean) // Remove any undefined attributes (deleted attributes)
  );

  /**
   * Check if a conversation is missing any required attributes
   *
   * @param {Object} conversationCustomAttributes - Current conversation's custom attributes
   * @returns {Object} - Analysis result with missing attributes info; when
   *   `unknown` is true the requirements could not be determined client-side
   *   and the caller must warn the user and defer to the server.
   */
  const checkMissingAttributes = (conversationCustomAttributes = {}) => {
    if (requirementsUnknown.value) {
      return { unknown: true, hasMissing: false, missing: [] };
    }

    // If no attributes are required, conversation can be resolved
    if (!requiredAttributes.value.length) {
      return { unknown: false, hasMissing: false, missing: [] };
    }

    // Find attributes that are missing or empty
    const missing = requiredAttributes.value.filter(attribute => {
      const value = conversationCustomAttributes[attribute.value];

      // For checkbox/boolean attributes, only check if the key exists
      if (attribute.type === ATTRIBUTE_TYPES.CHECKBOX) {
        return !(attribute.value in conversationCustomAttributes);
      }

      // For other attribute types, only consider null, undefined, empty string, or whitespace-only as missing
      // Allow falsy values like 0, false as they are valid filled values
      return value == null || String(value).trim() === '';
    });

    return {
      unknown: false,
      hasMissing: missing.length > 0,
      missing,
      all: requiredAttributes.value,
    };
  };

  return {
    requirementsUnknown,
    requiredAttributeKeys,
    requiredAttributes,
    checkMissingAttributes,
  };
}
